import { trpc } from '@/lib/trpc';
import { useState, useCallback, useEffect, useRef } from 'react';
import type { BatchStatus } from '../types';

export type BatchTab = 'open' | 'completed' | 'all';

export const TAB_STATUSES: Record<BatchTab, BatchStatus[] | undefined> = {
  open: ['OPEN', 'PICKING', 'PICKED', 'CATCHING', 'PACKING'],
  completed: ['COMPLETED'],
  all: undefined,
};

interface UseBatchListOptions {
  locationId: string | undefined;
  tab: BatchTab;
  search: string;
  sortOrder: 'newest' | 'oldest';
  pageSize?: number;
}

export function useBatchList({ locationId, tab, search, sortOrder, pageSize = 50 }: UseBatchListOptions) {
  const [currentPage, setCurrentPage] = useState(0);
  const offset = currentPage * pageSize;

  const filterKey = `${tab}-${search}-${sortOrder}`;
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      setCurrentPage(0);
    }
  }, [filterKey]);

  const query = trpc.productionOrders.listBatches.useQuery(
    {
      locationId: locationId!,
      statuses: TAB_STATUSES[tab],
      search: search || undefined,
      sortOrder,
      limit: pageSize,
      offset,
    },
    {
      enabled: !!locationId,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    }
  );

  const goToPage = useCallback((page: number) => {
    setCurrentPage(Math.max(0, page));
  }, []);

  const totalPages = Math.ceil((query.data?.pagination?.total ?? 0) / pageSize);

  return {
    batches: query.data?.batches ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    currentPage,
    totalPages,
    totalCount: query.data?.pagination?.total ?? 0,
    offset,
    goToPage,
  };
}
